import {
  AlertTriangle,
  BadgePercent,
  Building2,
  Check,
  ChevronDown,
  CreditCard,
  FileText,
  Gift,
  IndianRupee,
  PercentCircle,
  Receipt,
  RotateCcw,
  ShieldCheck,
  Sparkles,
  Stethoscope,
  StickyNote,
  TrendingDown,
  User,
} from "lucide-react";
import { useState } from "react";

interface AppliedOffer {
  id: string;
  name: string;
  discountAmount: number;
  freeQuantity: number;
}

interface Totals {
  subtotal: number;
  grandTotal: number;
  totalGst: number;
  manualDiscountTotal: number;
  schemeDiscountTotal: number;
  billDiscountAmount: number;
  roundOffAmount: number;
}

interface POSSidebarProps {
  customerPanel: React.ReactNode;
  selectedCustomerGstNumber?: string;
  notes: string;
  setNotes: (notes: string) => void;
  billDiscountType: "AMOUNT" | "PERCENT";
  setBillDiscountType: (type: "AMOUNT" | "PERCENT") => void;
  billDiscountValue: string;
  setBillDiscountValue: (value: string) => void;
  roundOffAmount: string;
  setRoundOffAmount: (value: string) => void;
  appliedOffers: AppliedOffer[];
  hasScheduledDrugs: boolean;
  uploadedPrescription: any;
  onUploadPrescription: () => void;
  scheduledDrugDoctorName: string;
  setScheduledDrugDoctorName: (name: string) => void;
  scheduledDrugPrescribedDate: string;
  setScheduledDrugPrescribedDate: (date: string) => void;
  totals: Totals;
  cartLength: number;
  clearCart: () => void;
  onPay: () => void;
  doctorName: string;
  setDoctorName: (value: string) => void;
  doctorRegistration: string;
  setDoctorRegistration: (value: string) => void;
  hospitalName: string;
  setHospitalName: (value: string) => void;
  isReverseCharge: boolean;
  setIsReverseCharge: (value: boolean) => void;
  reverseChargeReason: string;
  setReverseChargeReason: (value: string) => void;
}

function SidebarSection({
  icon: Icon,
  title,
  badge,
  defaultOpen = false,
  children,
}: {
  icon: React.ElementType;
  title: string;
  badge?: React.ReactNode;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <section className="overflow-hidden rounded-xl border border-slate-200 bg-white">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left transition-colors hover:bg-slate-50"
      >
        <Icon className="h-4 w-4 shrink-0 text-slate-400" />
        <span className="flex-1 text-xs font-semibold text-slate-700">
          {title}
        </span>
        {badge}
        <ChevronDown
          className={`h-3.5 w-3.5 text-slate-400 transition-transform duration-200 ${
            open ? "rotate-0" : "-rotate-90"
          }`}
        />
      </button>
      {open && (
        <div className="border-t border-slate-100 px-3 pb-3 pt-3">
          {children}
        </div>
      )}
    </section>
  );
}

export function POSSidebar({
  customerPanel,
  selectedCustomerGstNumber,
  notes,
  setNotes,
  billDiscountType: _billDiscountType,
  setBillDiscountType: _setBillDiscountType,
  billDiscountValue: _billDiscountValue,
  setBillDiscountValue: _setBillDiscountValue,
  roundOffAmount,
  setRoundOffAmount,
  appliedOffers,
  hasScheduledDrugs,
  uploadedPrescription,
  onUploadPrescription,
  scheduledDrugDoctorName,
  setScheduledDrugDoctorName,
  scheduledDrugPrescribedDate,
  setScheduledDrugPrescribedDate,
  totals,
  cartLength,
  clearCart,
  onPay,
  doctorName,
  setDoctorName,
  doctorRegistration,
  setDoctorRegistration,
  hospitalName,
  setHospitalName,
  isReverseCharge,
  setIsReverseCharge,
  reverseChargeReason,
  setReverseChargeReason,
}: POSSidebarProps) {
  const totalDiscount =
    totals.manualDiscountTotal +
    totals.schemeDiscountTotal +
    totals.billDiscountAmount;
  const cgst = totals.totalGst / 2;
  const sgst = totals.totalGst / 2;
  const hasBillDetails =
    Boolean(notes || doctorName || doctorRegistration || hospitalName) ||
    (parseFloat(roundOffAmount) || 0) !== 0;

  return (
    <aside className="grid h-full min-h-0 w-[320px] shrink-0 grid-rows-[minmax(0,1fr)_auto] overflow-hidden border-l border-slate-200 bg-slate-50/70 xl:w-[350px]">
      <div className="min-h-0 space-y-2 overflow-y-auto p-3">
        <SidebarSection icon={User} title="Customer" defaultOpen>
          {customerPanel}
        </SidebarSection>

        <SidebarSection
          icon={BadgePercent}
          title="Discount, round off & notes"
          badge={
            hasBillDetails ? (
              <span className="h-2 w-2 rounded-full bg-teal-500" />
            ) : undefined
          }
        >
          <div className="space-y-3">
            <label>
              <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                Round off
              </span>
              <div className="relative">
                <IndianRupee className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                <input
                  type="number"
                  step="0.01"
                  value={roundOffAmount}
                  onChange={(event) => setRoundOffAmount(event.target.value)}
                  placeholder="0.00 (use +/-)"
                  className="h-9 w-full rounded-lg border border-slate-200 bg-slate-50 pl-8 pr-3 text-sm font-medium tabular-nums outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-500/20"
                />
              </div>
            </label>

            <label>
              <span className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                <StickyNote className="h-3 w-3" />
                Invoice notes
              </span>
              <textarea
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                placeholder="Add optional invoice notes"
                rows={2}
                className="w-full resize-none rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none placeholder:text-slate-400 focus:border-teal-400 focus:ring-2 focus:ring-teal-500/20"
              />
            </label>

            <div className="grid grid-cols-1 gap-2 border-t border-slate-100 pt-3">
              <label>
                <span className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                  <Stethoscope className="h-3 w-3" />
                  Doctor
                </span>
                <input
                  value={doctorName}
                  onChange={(event) => setDoctorName(event.target.value)}
                  placeholder="Doctor name"
                  className="h-9 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-500/20"
                />
              </label>
              <label>
                <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                  Doctor registration
                </span>
                <input
                  value={doctorRegistration}
                  onChange={(event) =>
                    setDoctorRegistration(event.target.value)
                  }
                  placeholder="Registration number"
                  className="h-9 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-500/20"
                />
              </label>
              <label>
                <span className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                  <Building2 className="h-3 w-3" />
                  Hospital
                </span>
                <input
                  value={hospitalName}
                  onChange={(event) => setHospitalName(event.target.value)}
                  placeholder="Hospital name"
                  className="h-9 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-500/20"
                />
              </label>
            </div>
          </div>
        </SidebarSection>

        {selectedCustomerGstNumber && (
          <SidebarSection
            icon={ShieldCheck}
            title="Tax compliance"
            badge={
              isReverseCharge ? (
                <span className="rounded-full bg-violet-50 px-1.5 py-0.5 text-[9px] font-semibold text-violet-700">
                  Reverse charge
                </span>
              ) : undefined
            }
          >
            <label className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={isReverseCharge}
                onChange={(event) => setIsReverseCharge(event.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-slate-300 text-violet-600 focus:ring-violet-500"
              />
              <span>
                Apply reverse charge
                <span className="mt-1 block text-[10px] text-slate-500">
                  GSTIN {selectedCustomerGstNumber}
                </span>
              </span>
            </label>
            {isReverseCharge && (
              <label className="mt-3 block">
                <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                  Reason
                </span>
                <input
                  value={reverseChargeReason}
                  onChange={(event) =>
                    setReverseChargeReason(event.target.value)
                  }
                  placeholder="Section 9(3) or notified supply"
                  className="h-9 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-500/20"
                />
              </label>
            )}
          </SidebarSection>
        )}

        {appliedOffers.length > 0 && (
          <SidebarSection
            icon={Gift}
            title="Offers applied"
            defaultOpen
            badge={
              <span className="rounded-full bg-emerald-50 px-1.5 py-0.5 text-[9px] font-semibold text-emerald-700">
                {appliedOffers.length}
              </span>
            }
          >
            <div className="space-y-2">
              {appliedOffers.map((offer) => (
                <div
                  key={offer.id}
                  className="flex items-center justify-between rounded-lg border border-emerald-100 bg-emerald-50 px-2.5 py-2 text-xs"
                >
                  <span className="flex min-w-0 items-center gap-2 font-medium text-emerald-800">
                    <Sparkles className="h-3.5 w-3.5 shrink-0" />
                    <span className="truncate">{offer.name}</span>
                  </span>
                  <span className="shrink-0 text-right font-semibold text-emerald-700">
                    {offer.discountAmount > 0 && (
                      <span className="block">
                        −₹{offer.discountAmount.toFixed(2)}
                      </span>
                    )}
                    {offer.freeQuantity > 0 && (
                      <span className="block text-[9px]">
                        +{offer.freeQuantity} free
                      </span>
                    )}
                  </span>
                </div>
              ))}
            </div>
          </SidebarSection>
        )}

        {hasScheduledDrugs && (
          <SidebarSection
            icon={FileText}
            title="Scheduled drug details"
            defaultOpen
            badge={
              uploadedPrescription || scheduledDrugDoctorName ? (
                <ShieldCheck className="h-4 w-4 text-emerald-500" />
              ) : (
                <AlertTriangle className="h-4 w-4 text-amber-500" />
              )
            }
          >
            <div className="mb-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-2.5">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
              <p className="text-[11px] leading-relaxed text-amber-800">
                Add the prescription details where available. Missing details
                are recorded as warnings and do not stop the sale.
              </p>
            </div>

            <div className="space-y-2">
              <label>
                <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                  Prescribing doctor
                </span>
                <input
                  type="text"
                  value={scheduledDrugDoctorName}
                  onChange={(event) =>
                    setScheduledDrugDoctorName(event.target.value)
                  }
                  placeholder="Doctor name"
                  className="h-9 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-500/20"
                />
              </label>
              <label>
                <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                  Prescription date
                </span>
                <input
                  type="date"
                  value={scheduledDrugPrescribedDate}
                  onChange={(event) =>
                    setScheduledDrugPrescribedDate(event.target.value)
                  }
                  className="h-9 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-500/20"
                />
              </label>
            </div>

            {uploadedPrescription ? (
              <div className="mt-2 flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-2 text-[11px] font-semibold text-emerald-700">
                <Check className="h-3.5 w-3.5" />
                Prescription uploaded
              </div>
            ) : (
              <button
                type="button"
                onClick={onUploadPrescription}
                className="mt-2 w-full rounded-lg border border-teal-200 bg-teal-50 px-3 py-2 text-[11px] font-medium text-teal-700 transition hover:bg-teal-100"
              >
                Upload prescription
              </button>
            )}
          </SidebarSection>
        )}
      </div>

      <div className="shrink-0 border-t border-slate-200 bg-white p-3">
        <div className="rounded-xl border border-slate-200 p-3">
          <div className="mb-3 flex items-center gap-2">
            <Receipt className="h-4 w-4 text-slate-400" />
            <span className="text-xs font-semibold text-slate-700">
              Bill summary
            </span>
          </div>

          <dl className="space-y-2 text-xs">
            <div className="flex items-start justify-between gap-3">
              <dt className="text-slate-500">Taxable value</dt>
              <dd className="font-medium tabular-nums text-slate-700">
                ₹{totals.subtotal.toFixed(2)}
              </dd>
            </div>
            <div className="flex items-start justify-between gap-3">
              <dt className="text-slate-500">
                GST
                <span className="block text-[9px] text-slate-400">
                  CGST {cgst.toFixed(2)} · SGST {sgst.toFixed(2)}
                </span>
              </dt>
              <dd className="font-medium tabular-nums text-slate-700">
                ₹{totals.totalGst.toFixed(2)}
              </dd>
            </div>
            {totals.billDiscountAmount > 0 && (
              <div className="flex justify-between gap-3">
                <dt className="flex items-center gap-1.5 text-blue-600">
                  <PercentCircle className="h-3 w-3" />
                  Bill discount
                </dt>
                <dd className="font-medium tabular-nums text-blue-600">
                  −₹{totals.billDiscountAmount.toFixed(2)}
                </dd>
              </div>
            )}
            {totals.roundOffAmount !== 0 && (
              <div className="flex justify-between gap-3">
                <dt className="text-violet-600">Round off</dt>
                <dd className="font-medium tabular-nums text-violet-600">
                  {totals.roundOffAmount > 0 ? "+" : "−"}₹
                  {Math.abs(totals.roundOffAmount).toFixed(2)}
                </dd>
              </div>
            )}
          </dl>

          {totalDiscount > 0 && (
            <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-2 text-[11px] text-emerald-700">
              <span className="flex items-center gap-1.5">
                <TrendingDown className="h-3.5 w-3.5" />
                Customer saving
              </span>
              <span className="font-semibold tabular-nums">
                ₹{totalDiscount.toFixed(2)}
              </span>
            </div>
          )}
        </div>

        <div className="mt-2 rounded-xl bg-slate-900 p-3.5 text-white shadow-lg shadow-slate-300">
          <div className="flex items-end justify-between gap-3">
            <div>
              <span className="block text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                Total payable
              </span>
              <span className="mt-1 block text-[10px] text-emerald-400">
                {cartLength} product{cartLength === 1 ? "" : "s"} in this bill
              </span>
            </div>
            <span className="text-3xl font-bold tracking-tight tabular-nums">
              ₹{totals.grandTotal.toFixed(2)}
            </span>
          </div>
        </div>

        <div className="mt-2 grid grid-cols-[92px_minmax(0,1fr)] gap-2">
          <button
            type="button"
            onClick={clearCart}
            disabled={cartLength === 0}
            className="flex h-11 items-center justify-center gap-1.5 rounded-xl border border-red-200 bg-red-50 text-xs font-semibold text-red-700 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            Clear
          </button>
          <button
            type="button"
            onClick={onPay}
            disabled={cartLength === 0}
            className="flex h-11 items-center justify-between rounded-xl bg-teal-600 px-3 text-sm font-semibold text-white shadow-md shadow-teal-200 transition hover:bg-teal-700 active:scale-[0.99] disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400 disabled:shadow-none"
          >
            <span className="flex items-center gap-2">
              <CreditCard className="h-4 w-4" />
              Take payment
            </span>
            <kbd className="rounded bg-white/15 px-1.5 py-0.5 font-mono text-[10px]">
              F3
            </kbd>
          </button>
        </div>

        <div className="mt-2 flex items-center justify-between gap-2 px-1 text-[10px] text-slate-500">
          <span>Cash · UPI · Card · Split</span>
          <span>F1 clear · F2 search</span>
        </div>
      </div>
    </aside>
  );
}
